import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Input,
  OnDestroy,
  createComponent,
  input,
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';
import { TooltipPosition } from './tooltip-position';
import { TooltipTheme } from './tooltip-theme';

@Directive({
  selector: '[tooltip]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy {
  @Input() tooltip = '';
  @Input() position?: TooltipPosition = TooltipPosition.ABOVE;
  @Input() theme: TooltipTheme = TooltipTheme.DARK;
  @Input() visibleTooltip: boolean | HTMLElement = true;
  @Input() showDelay = 0;
  @Input() hideDelay = 0;
  @Input() shortcut = '';
  readonly tooltipAnchor = input<HTMLElement>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private componentRef: ComponentRef<any> | null = null;
  private showTimeout?: number;
  private hideTimeout?: number;
  private touchTimeout?: number;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.initializeTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.setHideTooltipTimeout();
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove($event: MouseEvent): void {
    if (
      this.componentRef !== null &&
      this.position === TooltipPosition.DYNAMIC
    ) {
      this.componentRef.setInput('left', $event.clientX);
      this.componentRef.setInput('top', $event.clientY);
      this.componentRef.setInput('tooltip', this.tooltip);
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(_$event: TouchEvent): void {
    // Do NOT preventDefault here — that suppresses the synthesized `click`
    // event on mobile, making every element with [tooltip] unclickable.
    window.clearTimeout(this.touchTimeout);
    this.touchTimeout = window.setTimeout(
      this.initializeTooltip.bind(this),
      500,
    );
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    window.clearTimeout(this.touchTimeout);
    this.setHideTooltipTimeout();
  }

  private initializeTooltip() {
    if (this.componentRef === null) {
      window.clearInterval(this.hideDelay);
      this.componentRef = createComponent(TooltipComponent, {
        environmentInjector: this.appRef.injector,
      });

      this.appRef.attachView(this.componentRef.hostView);
      const [tooltipDOMElement] = (
        this.componentRef.hostView as EmbeddedViewRef<unknown>
      ).rootNodes;

      this.setTooltipComponentProperties();

      document.body.appendChild(tooltipDOMElement);
      this.componentRef.instance.showInTopLayer();
      this.showTimeout = window.setTimeout(
        this.showTooltip.bind(this),
        this.showDelay,
      );
    }
  }

  private setTooltipComponentProperties() {
    if (this.componentRef !== null) {
      this.componentRef.setInput('tooltip', this.tooltip);
      this.componentRef.setInput('position', this.position);
      this.componentRef.setInput('theme', this.theme);
      this.componentRef.setInput('shortcut', this.shortcut);

      const anchorElement = this.tooltipAnchor() ?? this.elementRef.nativeElement;
      const { left, right, top, bottom } = anchorElement.getBoundingClientRect();

      switch (this.position) {
        case TooltipPosition.BELOW: {
          this.componentRef.setInput('left', Math.round((right - left) / 2 + left));
          this.componentRef.setInput('top', Math.round(bottom));
          break;
        }
        case TooltipPosition.BELOW_RIGHT: {
          this.componentRef.setInput('left', Math.round(left));
          this.componentRef.setInput('top', Math.round(bottom));
          break;
        }
        case TooltipPosition.BELOW_LEFT: {
          this.componentRef.instance.left = Math.round(right);
          this.componentRef.instance.top = Math.round(bottom);
          break;
        }
        case TooltipPosition.ABOVE: {
          this.componentRef.setInput('left', Math.round((right - left) / 2 + left));
          this.componentRef.setInput('top', Math.round(top));
          break;
        }
        case TooltipPosition.RIGHT: {
          this.componentRef.setInput('left', Math.round(right));
          this.componentRef.setInput('top', Math.round(top + (bottom - top) / 2));
          break;
        }
        case TooltipPosition.LEFT: {
          this.componentRef.setInput('left', Math.round(left));
          this.componentRef.setInput('top', Math.round(top + (bottom - top) / 2));
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  private showTooltip() {
    if (this.componentRef !== null) {
      this.componentRef.setInput('visible', this.isVisible());
    }
  }

  private isVisible(): boolean {
    if (!(this.visibleTooltip instanceof HTMLElement)) return this.visibleTooltip;
    const { scrollWidth, clientWidth, scrollHeight, clientHeight } = this.visibleTooltip;
    return scrollWidth > clientWidth || scrollHeight > clientHeight;
  }

  private setHideTooltipTimeout() {
    this.hideTimeout = window.setTimeout(
      this.destroy.bind(this),
      this.hideDelay,
    );
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(): void {
    if (this.componentRef !== null) {
      window.clearInterval(this.showTimeout);
      window.clearInterval(this.hideDelay);
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
